import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store/useAppStore';

type SubmitState = 'idle' | 'launching';

interface Props {
  onLaunch: (payload: Record<string, unknown>) => void;
  submitState: SubmitState;
}

const SOURCE_OPTIONS = [
  { value: 'openalex', label: 'OpenAlex' },
  { value: 'crossref', label: 'Crossref' },
  { value: 'semantic_scholar', label: 'Semantic Scholar' },
  { value: 'arxiv', label: 'arXiv' },
  { value: 'pubmed', label: 'PubMed' },
  { value: 'biorxiv', label: 'bioRxiv' },
  { value: 'medrxiv', label: 'medRxiv' },
] as const;

const JCR_OPTIONS = ['Q1', 'Q2', 'Q3', 'Q4'] as const;
const XR_OPTIONS = [
  { value: 'XR_1', label: 'XR 1' },
  { value: 'XR_2', label: 'XR 2' },
  { value: 'XR_3', label: 'XR 3' },
  { value: 'XR_4', label: 'XR 4' },
] as const;

export default function ConfigPanel({ onLaunch, submitState }: Props) {
  const { t } = useTranslation();
  const config = useAppStore((state) => state.config);
  const setConfig = useAppStore((state) => state.setConfig);
  const topic = config.topic;
  const currentYear = new Date().getFullYear();
  const isLaunching = submitState === 'launching';
  const parseYearInput = (value: string): number | null => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const toggleSource = (source: string) => {
    const exists = config.sources.includes(source);
    const nextSources = exists
      ? config.sources.filter((item) => item !== source)
      : [...config.sources, source];
    if (nextSources.length === 0) return;
    setConfig({ sources: nextSources });
  };

  const toggleJournalRank = (rank: string) => {
    const exists = config.journalRanks.includes(rank);
    setConfig({
      journalRanks: exists
        ? config.journalRanks.filter((item) => item !== rank)
        : [...config.journalRanks, rank],
    });
  };

  const parseImpactFactor = (value: string): number | null => {
    if (!value.trim()) return null;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  };

  const handleLaunch = () => {
    const trimmedTopic = topic.trim();
    if (!trimmedTopic) return;

    const yearFrom = config.yearFrom ?? currentYear - 4;
    const yearTo = config.yearTo ?? currentYear;

    setConfig({ topic: trimmedTopic, yearFrom, yearTo });
    onLaunch({
      topic: trimmedTopic,
      yearFrom,
      yearTo,
      sources: config.sources,
      journalRanks: config.journalRanks,
      minImpactFactor: config.minImpactFactor,
      citationStyle: config.citationStyle,
      outputFormats: config.outputFormats,
      enableKgExtraction: config.enableKgExtraction,
    });
  };

  return (
    <div className="center-config">
      <div className="center-config-card">
        <div className="center-config-kicker">Research Workspace</div>
        <h2 className="center-config-title">{t('app.title')}</h2>
        <p className="center-config-subtitle">{t('app.subtitle')}</p>

        <div className="center-search-stack">
          <label className="center-section-label" htmlFor="topic-input">{t('config.topic')}</label>
          <div className="center-config-row">
            <input
              id="topic-input"
              type="text"
              value={topic}
              onChange={(e) => setConfig({ topic: e.target.value })}
              placeholder={t('config.topicPH')}
              className="center-input"
              onKeyDown={(e) => e.key === 'Enter' && handleLaunch()}
            />
            <button className="center-launch-btn" onClick={handleLaunch} disabled={isLaunching || !topic.trim()}>
              {isLaunching ? t('config.launching') : t('config.launch')}
            </button>
          </div>
        </div>

        <div className="center-config-grid">
          <section className="center-config-panel">
            <div className="center-panel-head">
              <span className="center-section-label">{t('config.sources')}</span>
            </div>
            <div className="center-source-grid">
              {SOURCE_OPTIONS.map((source) => {
                const active = config.sources.includes(source.value);
                return (
                  <button
                    key={source.value}
                    type="button"
                    className={`center-source-pill ${active ? 'active' : ''}`}
                    onClick={() => toggleSource(source.value)}
                  >
                    <span className="center-source-dot" />
                    <span>{source.label}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="center-config-panel center-config-panel-compact center-year-panel">
            <div className="center-panel-head">
              <span className="center-section-label">{t('config.yearRange')}</span>
              <span className="center-panel-note">{config.yearFrom ?? currentYear - 4} 路 {config.yearTo ?? currentYear}</span>
            </div>
            <div className="center-year-track">
              <label className="center-year-field">
                <span>{t('config.yearFrom')}</span>
                <input
                  type="number"
                  min="1900"
                  max={currentYear + 1}
                  value={config.yearFrom ?? ''}
                  onChange={(e) => setConfig({ yearFrom: parseYearInput(e.target.value) })}
                  className="center-year-input"
                  aria-label={t('config.yearFrom')}
                />
              </label>
              <div className="center-year-bridge" aria-hidden="true">
                <span />
              </div>
              <label className="center-year-field">
                <span>{t('config.yearTo')}</span>
                <input
                  type="number"
                  min="1900"
                  max={currentYear + 1}
                  value={config.yearTo ?? ''}
                  onChange={(e) => setConfig({ yearTo: parseYearInput(e.target.value) })}
                  className="center-year-input"
                  aria-label={t('config.yearTo')}
                />
              </label>
            </div>
          </section>

          <section className="center-config-panel center-config-panel-compact center-journal-panel">
            <div className="center-panel-head">
              <span className="center-section-label">Journal quality</span>
              <span className="center-panel-note">JCR / XR categories</span>
            </div>
            <div className="center-jcr-grid">
              {JCR_OPTIONS.map((rank) => {
                const active = config.journalRanks.includes(rank);
                return (
                  <button
                    key={rank}
                    type="button"
                    className={`center-source-pill center-jcr-pill ${active ? 'active' : ''}`}
                    onClick={() => toggleJournalRank(rank)}
                  >
                    <span className="center-source-dot" />
                    <span>{rank}</span>
                  </button>
                );
              })}
              {XR_OPTIONS.map((rank) => {
                const active = config.journalRanks.includes(rank.value);
                return (
                  <button
                    key={rank.value}
                    type="button"
                    className={`center-source-pill center-jcr-pill ${active ? 'active' : ''}`}
                    onClick={() => toggleJournalRank(rank.value)}
                  >
                    <span className="center-source-dot" />
                    <span>{rank.label}</span>
                  </button>
                );
              })}
            </div>
            <label className="center-year-field">
              <span>Minimum IF</span>
              <input
                type="number"
                min="0"
                step="0.1"
                value={config.minImpactFactor ?? ''}
                onChange={(e) => setConfig({ minImpactFactor: parseImpactFactor(e.target.value) })}
                placeholder="涓嶉檺"
                className="center-year-input"
                aria-label="Minimum impact factor"
              />
            </label>
          </section>

          <section className="center-config-panel center-config-panel-compact center-style-panel">
            <div className="center-panel-head">
              <span className="center-section-label">{t('config.style')}</span>
            </div>
            <select
              value={config.citationStyle}
              onChange={(e) => setConfig({ citationStyle: e.target.value })}
              className="center-select"
            >
              <option value="IEEE">IEEE</option>
              <option value="APA">APA</option>
              <option value="GB/T 7714">GB/T 7714</option>
            </select>
          </section>
        </div>
      </div>
    </div>
  );
}
